import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Button,
  VStack,
  HStack,
  Text,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  FormHelperText,
  FormErrorMessage,
  Select,
  Checkbox,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Card,
  CardBody,
  CardHeader,
  Flex,
  useColorModeValue,
  useToast,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Progress,
  Badge,
  Collapse,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListIcon,
} from '@chakra-ui/react';
import { AttachmentIcon, ChevronDownIcon, ChevronUpIcon, ArrowForwardIcon, DeleteIcon } from '@chakra-ui/icons';
import { FaShare, FaExpand, FaCompress, FaComments, FaGitlab, FaFile } from 'react-icons/fa';
import { Config, Parameter, ConfigFormData } from '../../types/config';
import { EnhancedProxyService as ProxyService, FileUploadResponse } from '../../services/enhancedProxyService';
import { ConfigService } from '../../services/configService';
import GitLabService from '../../services/gitlabService';
import { useAppInfoContext } from '../../contexts/AppInfoContext';

interface InputAreaProps {
  config: Config | null;
  onResponse?: (content: string, isStreaming?: boolean) => void;
  onError?: (error: string) => void;
  onStartRequest?: () => void;
  onComplete?: () => void;
  onConfigUpdate?: (config: Config) => void;
  onGitLabFileUploadRequest?: (callback: (variable: string, gitlabUrl: string, filePath: string) => Promise<void>) => void;
  onGitLabSourcePathUpdate?: (sourcePath: string | null) => void;
  onToggleGitlabBrowser?: () => void;
  onInputChange?: (data: ConfigFormData) => void;
  initialData?: ConfigFormData;
  showSubmitButton?: boolean;
  onPanelCollapse?: (isCollapsed: boolean) => void;
  repoSelectedFile?: any;
}

export interface InputAreaRef {
  togglePanel: () => void;
  stopProcessing: () => void;
}

export const InputArea = forwardRef<InputAreaRef, InputAreaProps>(({
  config,
  onResponse,
  onError,
  onStartRequest,
  onComplete,
  onConfigUpdate,
  onGitLabFileUploadRequest,
  onGitLabSourcePathUpdate,
  onToggleGitlabBrowser,
  onInputChange,
  initialData,
  showSubmitButton = true,
  onPanelCollapse,
  repoSelectedFile,
}, ref) => {
  const [formData, setFormData] = useState<ConfigFormData>(initialData || {});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
  const [streamingContent, setStreamingContent] = useState('');
  const [shareCopied, setShareCopied] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isInputPanelCollapsed, setIsInputPanelCollapsed] = useState(true);
  const [showChatClient, setShowChatClient] = useState(false);
  const fileInputRefs = useRef<Record<string, HTMLInputElement>>({});
  const textareaRefs = useRef<Record<string, HTMLTextAreaElement>>({});
  const inputAreaContainerRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const { appInfo } = useAppInfoContext();
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const [paramsLoading, setParamsLoading] = useState(false);
  const [paramsError, setParamsError] = useState<string | null>(null);
  const [gitlabSourcePath, setGitlabSourcePath] = useState<string | null>(null);
  const [currentFileParameter, setCurrentFileParameter] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Expose functions to parent component
  useImperativeHandle(ref, () => ({
    togglePanel: toggleInputPanel,
    stopProcessing: () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
      setIsProcessing(false);
    },
  }));

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const textColor = useColorModeValue('gray.900', 'white');
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  // Helper function to format file sizes
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to extract file information from form data
  const getFileInfo = (value: any) => {
    if (!value) return [];
    
    const files = Array.isArray(value) ? value : [value];
    return files.map((file: any, index: number) => {
      // Use preserved metadata if available
      if (file._metadata) {
        return {
          id: file._metadata.id || `file-${index}`,
          name: file._metadata.name || 'Unknown file',
          size: file._metadata.size || 0,
          type: file._metadata.type || 'unknown',
          isGitlab: file._metadata.isGitlab || false
        };
      }
      
      // Fallback to original properties
      return {
        id: file.id || file.upload_file_id || `file-${index}`,
        name: file.name || file.fileName || file.originalName || 'Unknown file',
        size: file.size || 0,
        type: file.type || 'unknown',
        isGitlab: !!(file.gitlabUrl || file.sourcePath)
      };
    });
  };

  // Check if this is a chat application
  const isChatApplication = config?.requestUrl?.includes('chat-messages') || config?.applicationType === 'chat';

  // Function to remove a file from the form data
  const removeFile = (variable: string, fileId: string) => {
    const currentValue = formData[variable];
    if (!currentValue) return;

    const files = Array.isArray(currentValue) ? currentValue : [currentValue];
    const filteredFiles = files.filter((file: any, index: number) => {
      const currentId = file._metadata?.id || file.id || file.upload_file_id || `file-${index}`;
      return currentId !== fileId;
    });

    if (filteredFiles.length === 0) {
      // Remove the parameter entirely if no files left
      handleInputChange(variable, '');
    } else if (filteredFiles.length === 1 && !Array.isArray(currentValue)) {
      // Single file case
      handleInputChange(variable, filteredFiles[0]);
    } else {
      // Multiple files case
      handleInputChange(variable, filteredFiles);
    }

    toast({
      title: 'File removed',
      description: 'File has been removed from the upload list.',
      status: 'success',
      duration: 2000,
      isClosable: true,
    });
  };

  // Handle window resize and fullscreen changes
  useEffect(() => {
    const handleResize = () => {
      resizeAllTextareas();
    };

    const handleFullscreenChange = () => {
      const isFullscreenDocument = !!document.fullscreenElement;
      if (isFullscreen !== isFullscreenDocument) {
        setIsFullscreen(isFullscreenDocument);
        setTimeout(() => {
          resizeAllTextareas();
          if (!isFullscreenDocument) {
            setTimeout(() => {
              ensureExecuteButtonVisible();
            }, 100);
          }
        }, 100);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, [isFullscreen]);

  // Resize textareas when isFullscreen changes
  useEffect(() => {
    const timer = setTimeout(() => {
      resizeAllTextareas();
    }, 100);

    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // Resize textareas when parameters change (initial load)
  useEffect(() => {
    if (parameters.length > 0) {
      const timer = setTimeout(() => {
        resizeAllTextareas();
      }, 200); // Slightly longer delay for initial load

      return () => clearTimeout(timer);
    }
  }, [parameters]);

  // Call onInputChange when formData changes
  useEffect(() => {
    if (onInputChange) {
      onInputChange(formData);
    }
  }, [formData, onInputChange]);

  useEffect(() => {
    let cancelled = false;
    if (config) {
      // Always start docked when switching configs, will undock later if parameters are found
      setIsInputPanelCollapsed(true);
      onPanelCollapse?.(true);
      // If config already has parameters, use them directly (for CollapsibleInputPanel)
      if (config.parameters && config.parameters.length > 0) {
        console.log('Using existing config parameters:', config.parameters);
        setParameters(config.parameters);
        setParamsLoading(false);
        setParamsError(null);
        
        // Only initialize form data if no initialData was provided
        if (!initialData || Object.keys(initialData).length === 0) {
          const defaultData: ConfigFormData = {};
          config.parameters.forEach(param => {
            if (param.defaultValue !== undefined) {
              defaultData[param.variable] = param.defaultValue;
            } else {
              switch (param.type) {
                case 'checkbox':
                  defaultData[param.variable] = false;
                  break;
                case 'number':
                  defaultData[param.variable] = param.min || 0;
                  break;
                default:
                  defaultData[param.variable] = '';
              }
            }
          });
          setFormData(defaultData);
        }
        setErrors({});
        
        // Undock if parameters are present
        if (config.parameters.length > 0) {
          console.log('🔓 Auto-undocking input panel: Parameters found');
          setIsInputPanelCollapsed(false);
          onPanelCollapse?.(false);
        }
      } else {
        // Fetch parameters from API if not available
        setParamsLoading(true);
        setParamsError(null);
        ProxyService.fetchParameters(config.parametersUrl, config.apiKey, config.id)
          .then(fetched => {
            if (!cancelled) {
              console.log('Fetched parameters:', fetched);
              setParameters(fetched);
              // Initialize form data with default values from fetched params
              const defaultData: ConfigFormData = {};
              fetched.forEach(param => {
                // Check both 'default' (used by Dify) and 'defaultValue'
                let defaultValue = param.default ?? param.defaultValue;

                if (defaultValue !== undefined) {
                  // For file-type parameters, convert Dify's file object format if needed
                  if ((param.type === 'file' || param.type === 'file-list') && typeof defaultValue === 'object') {
                    // Check if it's a Dify file object that needs conversion
                    if (defaultValue.uploadedId || defaultValue.upload_file_id) {
                      const fileId = defaultValue.uploadedId || defaultValue.upload_file_id;
                      // Convert to the format expected by Dify API
                      defaultValue = {
                        type: defaultValue.supportFileType || 'document',
                        transfer_method: 'local_file',
                        url: '',
                        upload_file_id: fileId
                      };
                      console.log(`📎 Converted hidden file field "${param.variable}" to API format:`, defaultValue);
                    }
                  }

                  defaultData[param.variable] = defaultValue;
                  // Log hidden fields to help debugging
                  if (param.hide) {
                    console.log(`🔒 Hidden field "${param.variable}" initialized with default:`, defaultValue);
                  }
                } else {
                  switch (param.type) {
                    case 'checkbox':
                      defaultData[param.variable] = false;
                      break;
                    case 'number':
                      defaultData[param.variable] = param.min || 0;
                      break;
                    default:
                      defaultData[param.variable] = '';
                  }
                }
              });
              // Only set form data if no initialData was provided
              if (!initialData || Object.keys(initialData).length === 0) {
                setFormData(defaultData);
              }
              setErrors({});
              setParamsLoading(false);
              
              // Undock if parameters found from API
              if (fetched.length > 0) {
                console.log('🔓 Auto-undocking input panel: Parameters found from API');
                setIsInputPanelCollapsed(false);
                onPanelCollapse?.(false);
              }
            }
          })
          .catch(err => {
            if (!cancelled) {
              console.error('Error fetching parameters:', err);
              setParamsError('Failed to load input controls.');
              setParameters(config.parameters || []); // fallback
              // Also initialize form data from fallback
              const fallbackData: ConfigFormData = {};
              (config.parameters || []).forEach(param => {
                // Check both 'default' (used by Dify) and 'defaultValue'
                let defaultValue = param.default ?? param.defaultValue;

                if (defaultValue !== undefined) {
                  // For file-type parameters, convert Dify's file object format if needed
                  if ((param.type === 'file' || param.type === 'file-list') && typeof defaultValue === 'object') {
                    // Check if it's a Dify file object that needs conversion
                    if (defaultValue.uploadedId || defaultValue.upload_file_id) {
                      const fileId = defaultValue.uploadedId || defaultValue.upload_file_id;
                      // Convert to the format expected by Dify API
                      defaultValue = {
                        type: defaultValue.supportFileType || 'document',
                        transfer_method: 'local_file',
                        url: '',
                        upload_file_id: fileId
                      };
                      console.log(`📎 Converted hidden file field "${param.variable}" to API format (fallback):`, defaultValue);
                    }
                  }

                  fallbackData[param.variable] = defaultValue;
                  // Log hidden fields to help debugging
                  if (param.hide) {
                    console.log(`🔒 Hidden field "${param.variable}" initialized with default (fallback):`, defaultValue);
                  }
                } else {
                  switch (param.type) {
                    case 'checkbox':
                      fallbackData[param.variable] = false;
                      break;
                    case 'number':
                      fallbackData[param.variable] = param.min || 0;
                      break;
                    default:
                      fallbackData[param.variable] = '';
                  }
                }
              });
              // Only set form data if no initialData was provided
              if (!initialData || Object.keys(initialData).length === 0) {
                setFormData(fallbackData);
              }
              setErrors({});
              setParamsLoading(false);
              
              // Undock if fallback parameters are available
              if ((config.parameters || []).length > 0) {
                console.log('🔓 Auto-undocking input panel: Fallback parameters available');
                setIsInputPanelCollapsed(false);
                onPanelCollapse?.(false);
              }
            }
          })
          .finally(() => {
            if (!cancelled) setParamsLoading(false);
          });
      }
    } else {
      // No config selected - dock the panel and reset state
      setParameters([]);
      setParamsLoading(false);
      setParamsError(null);
      setIsInputPanelCollapsed(true);
      onPanelCollapse?.(true);
    }
    return () => { cancelled = true; };
  }, [config, initialData]);

  // Enhanced GitLab file upload function that uses the specific file parameter
  const handleGitLabFileUploadSmart = async (preferredVariable: string, gitlabUrl: string, filePath: string) => {
    // Priority order: 1) currentFileParameter (from Browse Repository button), 2) preferredVariable, 3) first file parameter
    let targetVariable: string | undefined = currentFileParameter || preferredVariable;
    let targetParam: Parameter | undefined;

    // If we have a target variable, validate it exists and is a file parameter
    if (targetVariable) {
      targetParam = parameters.find(param => param.variable === targetVariable && param.type === 'file');
      if (targetParam) {
        console.log('📁 Using specified file parameter:', targetVariable);
      } else {
        console.warn('⚠️ Target variable not found or not a file parameter:', targetVariable);
        targetVariable = undefined;
      }
    }

    // If no valid target variable, find the first file parameter
    if (!targetVariable) {
      targetParam = parameters.find(param => param.type === 'file');
      if (targetParam) {
        targetVariable = targetParam.variable;
        console.log('📁 Using first available file parameter:', targetVariable);
      } else {
        toast({
          title: 'No File Parameter',
          description: 'This configuration does not have any file upload parameters.',
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
        return;
      }
    }

    // Upload to the determined target variable with param for file type detection
    await handleGitLabFileUpload(targetVariable, gitlabUrl, filePath, targetParam);
    
    // Clear the current file parameter after upload
    setCurrentFileParameter(null);
  };

  // Expose GitLab file upload function to parent
  useEffect(() => {
    if (onGitLabFileUploadRequest) {
      onGitLabFileUploadRequest(handleGitLabFileUploadSmart);
    }
  }, [onGitLabFileUploadRequest, parameters, currentFileParameter]);

  // Effect: When repoSelectedFile changes, trigger upload logic
  useEffect(() => {
    if (repoSelectedFile && config) {
      // Find the first file parameter (or customize as needed)
      const fileParam = parameters.find(p => p.type === 'file');
      if (fileParam) {
        // Simulate file upload by setting formData for the file param
        setFormData(prev => ({ ...prev, [fileParam.variable]: repoSelectedFile }));
        toast({
          title: 'File selected from repository',
          description: repoSelectedFile.name || repoSelectedFile.path,
          status: 'info',
          duration: 3000,
          isClosable: true,
        });
      }
    }
  }, [repoSelectedFile, config, parameters, toast]);

  const validateForm = (): boolean => {
    if (!config) return false;

    const newErrors: Record<string, string> = {};

    parameters.forEach(param => {
      const value = formData[param.variable];

      // Required field validation
      if (param.required && (value === undefined || value === null || value === '')) {
        newErrors[param.variable] = `${param.label} is required`;
        return;
      }

      // Skip further validation if field is empty and not required
      if (value === undefined || value === null || value === '') {
        return;
      }

      // Number validation
      if (param.type === 'number') {
        const numValue = Number(value);
        if (isNaN(numValue)) {
          newErrors[param.variable] = 'Must be a valid number';
        } else {
          if (param.min !== undefined && numValue < param.min) {
            newErrors[param.variable] = `Must be at least ${param.min}`;
          }
          if (param.max !== undefined && numValue > param.max) {
            newErrors[param.variable] = `Must be at most ${param.max}`;
          }
        }
      }

      // Text/textarea length validation
      if ((param.type === 'text' || param.type === 'textarea') && typeof value === 'string') {
        if (param.min !== undefined && value.length < param.min) {
          newErrors[param.variable] = `Must be at least ${param.min} characters`;
        }
        if (param.max !== undefined && value.length > param.max) {
          newErrors[param.variable] = `Must be at most ${param.max} characters`;
        }
      }

      // File validation
      if (param.type === 'file' && value) {
        const maxFiles = param.max_length ?? param.metadata?.maxFiles ?? 1;
        const fileArray = Array.isArray(value) ? value : [value];
        
        if (fileArray.length > maxFiles) {
          newErrors[param.variable] = `Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed`;
        }
        
        // Validate file sizes if configuration is available
        const fileConfig = param.metadata?.fileUploadConfig;
        if (fileConfig) {
          for (const file of fileArray) {
            if (file && file.size) {
              const fileSizeMB = file.size / (1024 * 1024);
              const limit = fileConfig.file_size_limit || 35;
              
              if (fileSizeMB > limit) {
                newErrors[param.variable] = `File size must be less than ${limit}MB`;
                break;
              }
            }
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (variable: string, value: any) => {
    setFormData(prev => ({ ...prev, [variable]: value }));
    
    // Clear error for this field
    if (errors[variable]) {
      setErrors(prev => ({ ...prev, [variable]: '' }));
    }
  };

  const handleShare = async () => {
    if (!config) return;
    try {
      let shareUrl = '';
      let updatedConfig = config;
      if (config.isShared && config.shareId) {
        shareUrl = `${window.location.origin}/shared/${config.shareId}`;
      } else {
        // Generate share link
        const response = await ConfigService.generateShareLink(config.id, { daysToExpire: 30, permissions: 'execute' });
        // Fetch updated config from backend
        const updatedConfigs = await ConfigService.getAllConfigs();
        const newConfig = updatedConfigs.find(c => c.id === config.id);
        if (newConfig) {
          updatedConfig = newConfig;
          shareUrl = `${window.location.origin}/shared/${newConfig.shareId}`;
          // Update config in parent if callback provided
          if (onConfigUpdate) onConfigUpdate(newConfig);
        }
      }
      if (shareUrl) {
        await navigator.clipboard.writeText(shareUrl);
        setShareCopied(true);
        toast({
          title: 'Share link copied',
          description: 'The share link has been copied to your clipboard.',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
        setTimeout(() => setShareCopied(false), 2000);
      }
    } catch (error) {
      toast({
        title: 'Share failed',
        description: 'Failed to generate or copy share link.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Auto-resize textarea based on Angular implementation
  const autoResizeTextarea = (textarea: HTMLTextAreaElement) => {
    textarea.style.height = 'auto';
    const container = inputAreaContainerRef.current;
    if (!container) return;

    const cardBody = container.querySelector('.card-body') as HTMLElement;
    const buttonContainer = container.querySelector('.button-container') as HTMLElement;
    
    let availableHeight: number;
    if (isFullscreen) {
      // In fullscreen, use 95% of viewport height minus margin
      availableHeight = window.innerHeight * 0.95 - 24;
    } else {
      // In normal mode, fit above the button
      if (cardBody && buttonContainer) {
        const cardBodyRect = cardBody.getBoundingClientRect();
        const buttonRect = buttonContainer.getBoundingClientRect();
        availableHeight = buttonRect.top - cardBodyRect.top - 16;
      } else {
        // Fallback calculation
        availableHeight = window.innerHeight * 0.3;
      }
    }
    
    availableHeight = Math.max(availableHeight, 100); // Minimum 100px
    
    const scrollHeight = textarea.scrollHeight;
    const minHeight = 100;
    const newHeight = Math.min(Math.max(scrollHeight, minHeight), availableHeight);
    
    textarea.style.height = `${newHeight}px`;
    textarea.style.overflowY = scrollHeight > availableHeight ? 'auto' : 'hidden';
  };

  const resizeAllTextareas = () => {
    Object.values(textareaRefs.current).forEach(textarea => {
      if (textarea) {
        autoResizeTextarea(textarea);
      }
    });
  };

  const ensureExecuteButtonVisible = () => {
    const container = inputAreaContainerRef.current;
    if (!container) return;
    
    const executeButton = container.querySelector('.execute-button');
    if (!executeButton) return;
    
    const buttonRect = executeButton.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    
    if (buttonRect.bottom > containerRect.bottom) {
      executeButton.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'end',
        inline: 'nearest'
      });
    }
  };

  const toggleFullscreen = () => {
    const wasFullscreen = isFullscreen;
    setIsFullscreen(!isFullscreen);
    
    // Use setTimeout to allow state update to complete
    const delay = wasFullscreen && !isFullscreen ? 300 : 100;
    
    setTimeout(() => {
      resizeAllTextareas();
      
      if (!isFullscreen) {
        setTimeout(() => {
          ensureExecuteButtonVisible();
        }, 100);
      }
    }, delay);
  };

  const toggleInputPanel = () => {
    const newCollapsedState = !isInputPanelCollapsed;
    setIsInputPanelCollapsed(newCollapsedState);
    onPanelCollapse?.(newCollapsedState); // Signal to parent about the state change
  };

  // Determine file type based on extension and parameter config from Dify
  const getFileTypeForApi = (fileName: string, param?: Parameter) => {
    const ext = fileName.split('.').pop()?.toLowerCase();

    // Check if param has allowed file types from Dify
    const allowedTypes = param?.metadata?.allowedFileTypes || [];
    const allowedExtensions = param?.metadata?.allowedFileExtensions || [];

    // Standard image detection
    if (["png", "jpg", "jpeg", "gif", "bmp", "webp", "svg"].includes(ext || "")) {
      return "image";
    }

    // Audio files
    if (["mp3", "m4a", "wav", "amr", "mpga"].includes(ext || "")) {
      return "audio";
    }

    // Video files
    if (["mp4", "mov", "mpeg", "webm"].includes(ext || "")) {
      return "video";
    }

    // Document files (standard Dify document types)
    const documentExtensions = ["txt", "md", "mdx", "markdown", "pdf", "html",
      "xlsx", "xls", "doc", "docx", "csv", "eml", "msg", "pptx", "ppt", "xml", "epub"];
    if (documentExtensions.includes(ext || "")) {
      return "document";
    }

    // If file has custom extension and custom extensions are allowed, use "custom"
    if (allowedExtensions.length > 0) {
      return "custom";
    }

    // Default fallback - check if document is allowed
    if (allowedTypes.includes('document')) {
      return "document";
    }

    // For unknown extensions, use "custom" (for Dify's "Other file types")
    return "custom";
  };

  const handleFileUpload = async (variable: string, files: File[] | File, param?: Parameter) => {
    if (!config) return;

    try {
      const fileArray = Array.isArray(files) ? files : [files];
      setUploadProgress(prev => ({ ...prev, [variable]: 0 }));

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[variable] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return { ...prev, [variable]: current + 10 };
        });
      }, 100);

      // Get upload URL based on config endpoint
      const uploadUrl = ProxyService.getUploadUrl(config.endpointUrl || config.requestUrl);

      // Upload files
      const uploadedFiles = await ProxyService.uploadFiles(fileArray, uploadUrl, config.id);

      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [variable]: 100 }));

      // If multiple files, use the type of the first file
      const fileType = uploadedFiles.length > 0 ? getFileTypeForApi(uploadedFiles[0].name, param) : 'document';
      const apiFormatFiles = ProxyService.convertFilesToApiFormat(uploadedFiles, fileType);
      
      // Preserve original file metadata for UI display
      if (Array.isArray(apiFormatFiles)) {
        apiFormatFiles.forEach((apiFile, index) => {
          if (uploadedFiles[index]) {
            apiFile._metadata = {
              name: uploadedFiles[index].name,
              size: uploadedFiles[index].size,
              id: uploadedFiles[index].id,
              isGitlab: false
            };
          }
        });
      } else if (apiFormatFiles && uploadedFiles[0]) {
        apiFormatFiles._metadata = {
          name: uploadedFiles[0].name,
          size: uploadedFiles[0].size,
          id: uploadedFiles[0].id,
          isGitlab: false
        };
      }
      
      handleInputChange(variable, apiFormatFiles);

      toast({
        title: 'File uploaded',
        description: `${fileArray.length} file(s) uploaded successfully.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      // For chat applications, dock the panel and prompt user to ask a question
      if (isChatApplication) {
        setIsInputPanelCollapsed(true);
        onPanelCollapse?.(true);
        toast({
          title: 'Ready for your question',
          description: 'Now ask your question in the chat area below.',
          status: 'info',
          duration: 5000,
          isClosable: true,
        });
      }

      // Clear progress after 2 seconds
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [variable]: 0 }));
      }, 2000);
    } catch (error: any) {
      setUploadProgress(prev => ({ ...prev, [variable]: 0 }));
      toast({
        title: 'Upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleGitLabFileUpload = async (variable: string, gitlabUrl: string, filePath: string, param?: Parameter) => {
    if (!config) return;

    try {
      setUploadProgress(prev => ({ ...prev, [variable]: 0 }));

      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          const current = prev[variable] || 0;
          if (current >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          return { ...prev, [variable]: current + 10 };
        });
      }, 100);

      // Get upload URL based on config endpoint
      const uploadUrl = ProxyService.getUploadUrl(config.endpointUrl || config.requestUrl);

      // Upload file from GitLab
      const result = await ProxyService.uploadFileFromGitLab(gitlabUrl, filePath, uploadUrl, config.id);

      clearInterval(progressInterval);
      setUploadProgress(prev => ({ ...prev, [variable]: 100 }));

      if (result.success && result.data) {
        // Determine file type based on extension and param config
        const fileName = filePath.includes('/') ? filePath.substring(filePath.lastIndexOf('/') + 1) : filePath;
        const fileType = getFileTypeForApi(fileName, param);
        // Convert uploaded file response to API format and store in form data
        const apiFormatFile = ProxyService.convertFilesToApiFormat([result.data], fileType);
        
        // Preserve original file metadata for UI display
        if (apiFormatFile && result.data) {
          const fileName = filePath.includes('/') ? filePath.substring(filePath.lastIndexOf('/') + 1) : filePath;
          apiFormatFile._metadata = {
            name: fileName,
            size: result.data.size || 0,
            id: result.data.id,
            isGitlab: true
          };
        }
        
        // Remove _metadata before updating form data
        const cleanedApiFormatFile = removeMetadata(apiFormatFile);
        handleInputChange(variable, cleanedApiFormatFile);
        
        // Store the GitLab source path for later use in saving generated documents
        // Extract directory path from the uploaded file path
        const directoryPath = filePath.includes('/') ? filePath.substring(0, filePath.lastIndexOf('/')) : '';
        setGitlabSourcePath(directoryPath);
        onGitLabSourcePathUpdate?.(directoryPath);
        
        // Store source file information for commit messages
        const sourceFileInfo = {
          gitlabUrl: gitlabUrl,
          filePath: filePath,
          fileName: filePath.includes('/') ? filePath.substring(filePath.lastIndexOf('/') + 1) : filePath,
          uploadedAt: new Date().toISOString()
        };
        sessionStorage.setItem('lastUploadedFile', JSON.stringify(sourceFileInfo));
        
        console.log('📁 GitLab source path stored:', directoryPath);
        console.log('📄 Source file info stored:', sourceFileInfo);
        
        toast({
          title: 'GitLab file uploaded',
          description: `${result.sourceFile?.fileName} uploaded successfully from GitLab.`,
          status: 'success',
          duration: 3000,
          isClosable: true,
        });

        // For chat applications, dock the panel and prompt user to ask a question
        if (isChatApplication) {
          setIsInputPanelCollapsed(true);
          onPanelCollapse?.(true);
          toast({
            title: 'Ready for your question',
            description: 'Now ask your question in the chat area below.',
            status: 'info',
            duration: 5000,
            isClosable: true,
          });
        }
      } else {
        throw new Error(result.error || 'GitLab upload failed');
      }
      
      // Clear progress after 2 seconds
      setTimeout(() => {
        setUploadProgress(prev => ({ ...prev, [variable]: 0 }));
      }, 2000);
    } catch (error: any) {
      setUploadProgress(prev => ({ ...prev, [variable]: 0 }));
      toast({
        title: 'GitLab upload failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleUpdateInputs = () => {
    if (!config || !validateForm()) {
      return;
    }

    // Notify parent about the updated inputs
    onInputChange?.(formData);
    
    // Show success message
    toast({
      title: "Inputs Updated",
      description: "Your input configuration has been saved and will be used for chat requests.",
      status: "success",
      duration: 3000,
      isClosable: true,
    });
  };

  // Utility to recursively remove _metadata from any object/array
  const removeMetadata = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(removeMetadata);
    } else if (obj && typeof obj === 'object') {
      const newObj: any = {};
      for (const key in obj) {
        if (key !== '_metadata') {
          newObj[key] = removeMetadata(obj[key]);
        }
      }
      return newObj;
    }
    return obj;
  };

  const handleSubmit = async () => {
    if (!config || !validateForm()) {
      return;
    }

    // Prevent rapid successive clicks
    if (isProcessing) {
      console.log('Request already in progress, ignoring click');
      return;
    }

    if (isChatApplication) {
      // For chat applications, dock the input panel and notify parent
      setIsInputPanelCollapsed(true);
      onPanelCollapse?.(true); // Signal to parent that panel is collapsed
      onStartRequest?.(); // This will signal to parent to show chat in ResponseViewer
      return;
    }

    // Force cleanup of any existing request before starting new one
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    setIsProcessing(true);
    setStreamingContent('');
    
    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();
    
    // Notify parent that request is starting - this should trigger complete reset
    onStartRequest?.();

    // Dock the panel and show toast for workflow mode
    setIsInputPanelCollapsed(true);
    onPanelCollapse?.(true);
    toast({
      title: 'Processing your request',
      description: 'No further input needed. Your response will appear below.',
      status: 'info',
      duration: 4000,
      isClosable: true,
    });

    try {
      // Remove all _metadata fields before sending
      const cleanedFormData = removeMetadata(formData);
      await ProxyService.processRequestWithCitations(
        config,
        cleanedFormData,
        // onMessage callback for streaming
        (chunk: string) => {
          setStreamingContent(prev => prev + chunk);
          onResponse?.(chunk, true); // true indicates streaming
        },
        // onError callback
        (error: string) => {
          onError?.(error);
        },
        // onComplete callback
        () => {
          setIsProcessing(false);
          abortControllerRef.current = null;
          onComplete?.();
          toast({
            title: 'Processing complete',
            description: 'Your document has been generated successfully.',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        },
        // abortController for stopping requests
        abortControllerRef.current
      );
    } catch (error: any) {
      setIsProcessing(false);
      abortControllerRef.current = null;
      onError?.(error.message);
      toast({
        title: 'Processing failed',
        description: error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleSaveAndClose = () => {
    if (validateForm()) {
      setShowChatClient(true);
    }
  };

  const handleCloseChat = () => {
    setShowChatClient(false);
  };

  const renderFormControl = (param: Parameter) => {
    // Skip rendering if field is marked as hidden
    // Hidden fields will still have their default values preserved in formData
    if (param.hide) {
      console.log(`🙈 Skipping hidden field: ${param.variable}`, param.default || param.defaultValue);
      return null;
    }

    const value = formData[param.variable];
    const error = errors[param.variable];
    const progress = uploadProgress[param.variable];

    // Defensive: fallback to param.variable if label is missing
    const label = param.label || param.variable || 'Input';

    const baseProps = {
      id: param.variable,
      value: value || '',
      placeholder: param.placeholder || 'Choose an option...',
      isRequired: param.required,
      isInvalid: !!error,
    };

    // Normalize type for user_input_form
    let type = param.type;
    if (type === 'text-input') type = 'text';
    if (type === 'paragraph') type = 'textarea';
    if (type === 'file-list') type = 'file-list';

    switch (type) {
      case 'textarea':
        const textareaValue = value || '';
        const charCount = textareaValue.length;
        const maxLength = param.max_length || param.max;
        return (
          <FormControl key={param.variable} isInvalid={!!error}>
            <FormLabel htmlFor={param.variable} color={textColor}>
              {label}
              {param.required && <Text as="span" color="red.500" ml={1}>*</Text>}
              {maxLength && (
                <Text as="span" fontSize="xs" color={mutedColor} ml={2}>
                  ({charCount} characters)
                </Text>
              )}
            </FormLabel>
            <Textarea
              {...baseProps}
              ref={(el) => {
                if (el) {
                  textareaRefs.current[param.variable] = el;
                }
              }}
              value={textareaValue}
              minHeight="100px"
              resize="vertical"
              maxLength={maxLength}
              onChange={(e) => {
                handleInputChange(param.variable, e.target.value);
                // Auto-resize on input
                setTimeout(() => {
                  if (textareaRefs.current[param.variable]) {
                    autoResizeTextarea(textareaRefs.current[param.variable]);
                  }
                }, 0);
              }}
              onFocus={() => {
                // Ensure proper sizing on focus
                setTimeout(() => {
                  if (textareaRefs.current[param.variable]) {
                    autoResizeTextarea(textareaRefs.current[param.variable]);
                  }
                }, 0);
              }}
              style={{
                transition: 'height 0.2s ease',
                overflowY: 'hidden'
              }}
            />
            {param.description && (
              <FormHelperText color={mutedColor}>{param.description}</FormHelperText>
            )}
            {maxLength && charCount > maxLength && (
              <FormHelperText color="red.500">
                Maximum length exceeded by {Math.round(((charCount - maxLength) / maxLength) * 100)}%
              </FormHelperText>
            )}
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        );
      case 'number':
        return (
          <FormControl key={param.variable} isInvalid={!!error}>
            <FormLabel htmlFor={param.variable} color={textColor}>
              {label}
              {param.required && <Text as="span" color="red.500" ml={1}>*</Text>}
            </FormLabel>
            <NumberInput
              value={value || ''}
              min={param.min}
              max={param.max}
              step={param.step || 1}
              onChange={(valueString) => handleInputChange(param.variable, valueString)}
            >
              <NumberInputField {...baseProps} />
              <NumberInputStepper>
                <NumberIncrementStepper />
                <NumberDecrementStepper />
              </NumberInputStepper>
            </NumberInput>
            {param.description && (
              <FormHelperText color={mutedColor}>{param.description}</FormHelperText>
            )}
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        );
      case 'select':
        // Support options as array of strings or array of objects
        let options = param.options || [];
        if (Array.isArray(options) && options.length > 0 && typeof options[0] === 'string') {
          options = (options as unknown as string[]).map(opt => ({ label: opt, value: opt }));
        }
        return (
          <FormControl key={param.variable} isInvalid={!!error}>
            <FormLabel htmlFor={param.variable} color={textColor}>
              {label}
              {param.required && <Text as="span" color="red.500" ml={1}>*</Text>}
            </FormLabel>
            <Select
              id={param.variable}
              value={value || ''}
              isRequired={param.required}
              isInvalid={!!error}
              onChange={(e) => handleInputChange(param.variable, e.target.value)}
            >
              <option value="">Choose an option...</option>
              {options?.map((option) => (
                <option key={option.value.toString()} value={option.value.toString()}>
                  {option.label || option.value}
                </option>
              ))}
            </Select>
            {param.description && (
              <FormHelperText color={mutedColor}>{param.description}</FormHelperText>
            )}
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        );
      case 'file-list':
        // Multi-file upload
        // Use the same logic as 'file', but force multi-file
        // ... fall through to file ...
      case 'file':
        const isMultiFile = type === 'file-list' || param.type === 'file-list' || (param.max_length && param.max_length > 1);
        const maxFiles = param.max_length ?? param.metadata?.maxFiles ?? 1;
        const allowedMethods = param.allowed_file_upload_methods || param.metadata?.allowedUploadMethods || ['local_file'];
        const supportsLocalFile = allowedMethods.includes('local_file');
        const supportsRemoteUrl = allowedMethods.includes('remote_url');
        return (
          <FormControl key={param.variable} isInvalid={!!error}>
            <FormLabel htmlFor={param.variable} color={textColor}>
              {label}
              {param.required && <Text as="span" color="red.500" ml={1}>*</Text>}
              {isMultiFile && (
                <Badge ml={2} colorScheme="blue" variant="outline" fontSize="xs">
                  Max {maxFiles} files
                </Badge>
              )}
            </FormLabel>
            <VStack spacing={3} align="stretch">
              {/* Local file upload */}
              {supportsLocalFile && (
                <VStack spacing={2} align="stretch">
                  <HStack>
                    <Button
                      leftIcon={<AttachmentIcon />}
                      size="sm"
                      variant="outline"
                      onClick={() => fileInputRefs.current[param.variable]?.click()}
                      isDisabled={progress > 0 && progress < 100}
                    >
                      {isMultiFile ? 'Choose Files' : 'Choose File'}
                    </Button>
                    {onToggleGitlabBrowser && (
                      <Button
                        leftIcon={<FaGitlab />}
                        size="sm"
                        variant="outline"
                        colorScheme="orange"
                        onClick={() => {
                          // Store the current parameter variable for GitLab uploads
                          setCurrentFileParameter(param.variable);
                          console.log('📁 Setting current file parameter:', param.variable);
                          onToggleGitlabBrowser();
                        }}
                        isDisabled={progress > 0 && progress < 100}
                        title="Browse GitLab repository"
                      >
                        Browse Repository
                      </Button>
                    )}
                    {value && (
                      <Badge colorScheme="green" variant="subtle">
                        {isMultiFile ? `${Array.isArray(value) ? value.length : 1} file(s)` : 'File uploaded'}
                      </Badge>
                    )}
                  </HStack>
                  {progress > 0 && progress < 100 && (
                    <Progress value={progress} colorScheme="blue" size="sm" />
                  )}
                  <input
                    ref={(el) => {
                      if (el) fileInputRefs.current[param.variable] = el;
                    }}
                    type="file"
                    accept={param.accept}
                    multiple={!!isMultiFile}
                    style={{ display: 'none' }}
                    onChange={(e) => {
                      const files = Array.from(e.target.files || []);
                      if (files.length > 0) {
                        if (isMultiFile) {
                          // Handle multiple files
                          if (files.length > maxFiles) {
                            toast({
                              title: 'Too many files',
                              description: `Maximum ${maxFiles} files allowed`,
                              status: 'error',
                              duration: 3000,
                              isClosable: true,
                            });
                            return;
                          }
                          // Upload all files at once
                          handleFileUpload(param.variable, files, param);
                        } else {
                          handleFileUpload(param.variable, files[0], param);
                        }
                      }
                      // Clear the input value to allow selecting the same file again
                      if (e.target) {
                        e.target.value = '';
                      }
                    }}
                  />
                </VStack>
              )}
              {/* Remote URL input */}
              {supportsRemoteUrl && (
                <VStack spacing={2} align="stretch">
                  <Text fontSize="sm" color={mutedColor}>Or enter file URL:</Text>
                  <Input
                    placeholder="https://example.com/file.pdf"
                    value={typeof value === 'string' && value.startsWith('http') ? value : ''}
                    onChange={(e) => handleInputChange(param.variable, e.target.value)}
                  />
                </VStack>
              )}
              {/* File type restrictions info */}
              {(param.allowed_file_types?.length || param.allowed_file_extensions?.length || param.metadata?.allowedFileTypes?.length || param.metadata?.allowedFileExtensions?.length) && (
                <Text fontSize="xs" color={mutedColor}>
                  Allowed: {param.allowed_file_types?.join(', ') || param.allowed_file_extensions?.join(', ') || param.metadata?.allowedFileTypes?.join(', ') || param.metadata?.allowedFileExtensions?.join(', ')}
                </Text>
              )}
              
              {/* Uploaded Files List */}
              {value && (
                <Box>
                  <Text fontSize="sm" fontWeight="medium" color={textColor} mb={2}>
                    Uploaded Files ({getFileInfo(value).length})
                  </Text>
                  <List spacing={2}>
                    {getFileInfo(value).map((file) => (
                      <ListItem
                        key={file.id}
                        p={2}
                        border="1px"
                        borderColor={borderColor}
                        borderRadius="md"
                        bg={useColorModeValue('gray.50', 'gray.700')}
                      >
                        <HStack justify="space-between">
                          <HStack spacing={2}>
                            <ListIcon as={FaFile} color={file.isGitlab ? 'orange.500' : 'blue.500'} />
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm" fontWeight="medium">
                                {file.name}
                              </Text>
                              <HStack spacing={2}>
                                <Text fontSize="xs" color={mutedColor}>
                                  {formatFileSize(file.size)}
                                </Text>
                                {file.isGitlab && (
                                  <Badge size="xs" colorScheme="orange" variant="subtle">
                                    GitLab
                                  </Badge>
                                )}
                              </HStack>
                            </VStack>
                          </HStack>
                          <IconButton
                            aria-label="Remove file"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => removeFile(param.variable, file.id)}
                          />
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </VStack>
            {param.description && (
              <FormHelperText color={mutedColor}>{param.description}</FormHelperText>
            )}
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        );
      default: // text
        const textValue = value || '';
        const textCharCount = textValue.length;
        const textMaxLength = param.max_length || param.max;
        return (
          <FormControl key={param.variable} isInvalid={!!error}>
            <FormLabel htmlFor={param.variable} color={textColor}>
              {label}
              {param.required && <Text as="span" color="red.500" ml={1}>*</Text>}
              {textMaxLength && (
                <Text as="span" fontSize="xs" color={mutedColor} ml={2}>
                  ({textCharCount} characters)
                </Text>
              )}
            </FormLabel>
            <Input
              {...baseProps}
              value={textValue}
              type="text"
              maxLength={textMaxLength}
              onChange={(e) => handleInputChange(param.variable, e.target.value)}
            />
            {param.description && (
              <FormHelperText color={mutedColor}>{param.description}</FormHelperText>
            )}
            {textMaxLength && textCharCount > textMaxLength && (
              <FormHelperText color="red.500">
                Maximum length exceeded by {Math.round(((textCharCount - textMaxLength) / textMaxLength) * 100)}%
              </FormHelperText>
            )}
            {error && <FormErrorMessage>{error}</FormErrorMessage>}
          </FormControl>
        );
    }
  };


  // For chat applications, don't redirect here - let the parent handle it in ResponseViewer

  if (!config) {
    return (
      <Card bg={cardBg} h="400px">
        <Center h="full">
          <VStack spacing={4}>
            <Text color={mutedColor} fontSize="lg">
              Select a document to begin
            </Text>
            <Text color={mutedColor} fontSize="sm" textAlign="center">
              Choose a document from the sidebar to configure input parameters
            </Text>
          </VStack>
        </Center>
      </Card>
    );
  }

  return (
    <Box className="input-area-container" 
         ref={inputAreaContainerRef}
         position={isFullscreen ? 'fixed' : 'relative'} 
         top={isFullscreen ? '0' : 'auto'} 
         left={isFullscreen ? '0' : 'auto'} 
         w={isFullscreen ? '100vw' : 'auto'} 
         h={isFullscreen ? '100vh' : 'auto'} 
         zIndex={isFullscreen ? 9999 : 'auto'} 
         borderRadius={isFullscreen ? '0' : 'md'}
         bg={cardBg}
         display="flex"
         flexDirection="column"
         boxShadow="sm"
         border="1px"
         borderColor={borderColor}
         overflow="hidden"
         style={{
           transition: 'all 0.3s ease'
         }}>
      <Box className="card" display="flex" flexDirection="column" h="full">
        <Box className="card-header" 
             p={4} 
             bg={useColorModeValue('gray.50', 'gray.900')}
             borderBottom="1px" 
             borderColor={borderColor}
             position="sticky"
             top="0"
             zIndex={10}
             flexShrink={0}>
          <Flex justify="space-between" align="center" w="full" minW={0}>
            <HStack className="header-title-section" align="center" spacing={3} flex="1" minW={0}>
              {config.icon ? (
                <Box
                  w="36px"
                  h="36px"
                  borderRadius="md"
                  bg="white"
                  border="1px"
                  borderColor="gray.200"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  overflow="hidden"
                  flexShrink={0}
                  mr={3}
                >
                  <img 
                    src={config.icon} 
                    alt="icon" 
                    style={{ width: '32px', height: '32px', objectFit: 'contain' }}
                  />
                </Box>
              ) : (
                <Box
                  w="36px"
                  h="36px"
                  bg="gray.700"
                  color="white"
                  fontWeight="bold"
                  fontSize="xl"
                  borderRadius="md"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  userSelect="none"
                  flexShrink={0}
                  mr={3}
                >
                  {config.name?.charAt(0).toUpperCase() || 'C'}
                </Box>
              )}
              <Text 
                fontSize="xl" 
                fontWeight="semibold" 
                color={textColor}
                isTruncated
                flex="1"
                minW={0}
              >
                {config.name || 'Code Generator'}
              </Text>
            </HStack>
            <Box className="header-actions">
              <HStack spacing={2}>
                {isProcessing && (
                  <Box className="loading-indicator">
                    <HStack spacing={2}>
                      <Spinner size="sm" />
                      <Text fontSize="sm" color={mutedColor}>Processing</Text>
                    </HStack>
                  </Box>
                )}
                {/* Show dock toggle for all applications */}
                <IconButton
                  className="action-button"
                  size="sm"
                  variant="outline"
                  onClick={toggleInputPanel}
                  icon={isInputPanelCollapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
                  aria-label="Toggle input panel"
                />
                <Button
                  className="action-button"
                  size="sm"
                  variant="outline"
                  onClick={toggleFullscreen}
                  leftIcon={isFullscreen ? <FaCompress /> : <FaExpand />}
                  isDisabled={parameters.length === 0}
                >
                  {isFullscreen ? 'Exit' : 'Fullscreen'}
                </Button>
                <Button
                  className="action-button"
                  size="sm"
                  variant="outline"
                  leftIcon={<FaShare />}
                  onClick={handleShare}
                  title="Copy and Share Link to this document"
                >
                  {shareCopied ? 'Copied!' : 'Share'}
                </Button>
              </HStack>
            </Box>
          </Flex>
        </Box>

        <Box className="card-body" flex="1" overflowY="auto" p={0} minH="0">
          {/* Error message */}
          {!config && (
            <Alert status="info" borderRadius="md" m={4}>
              <AlertIcon />
              <VStack spacing={1} align="start" w="full">
                <Text fontWeight="semibold">No active document selected</Text>
                <Text fontSize="sm">Please configure a document in Settings</Text>
              </VStack>
            </Alert>
          )}

          {/* Loading indicator for collapsed state */}
          {isInputPanelCollapsed && paramsLoading && (
            <Box className="loading-indicator-collapsed" p={4} bg={cardBg}>
              <Center>
                <HStack spacing={3}>
                  <Spinner size="sm" color="blue.500" />
                  <Text color={mutedColor} fontSize="sm">Loading Inputs...</Text>
                </HStack>
              </Center>
            </Box>
          )}

          {/* Input Controls Section */}
          <Collapse in={!isInputPanelCollapsed} animateOpacity>
            <Box className="input-controls" p={6}>
            {/* Loading state for parameters */}
            {paramsLoading && (
              <Box className="loading-controls">
                <Center py={8}>
                  <VStack spacing={3}>
                    <Spinner size="lg" color="blue.500" />
                    <Text color={mutedColor}>Loading Inputs...</Text>
                  </VStack>
                </Center>
              </Box>
            )}

            {/* Error state for parameters */}
            {paramsError && !paramsLoading && (
              <Alert borderRadius="md" mb={4} bg="red.50" borderColor="red.200">
                <AlertIcon color="red.500" />
                <VStack spacing={1} align="start" w="full">
                  <Text fontWeight="semibold">Failed to load input controls</Text>
                  <Text fontSize="sm">{paramsError}</Text>
                </VStack>
              </Alert>
            )}

            {/* Input Controls - Use Same Layout for Both Chat and Document Applications */}
            <>
              {/* Dynamic form fields */}
              {!paramsLoading && !paramsError && parameters.length > 0 ? (
                <Box className="input-controls-grid">
                    {parameters.map(renderFormControl)}
                  </Box>
                ) : !paramsLoading && !paramsError && parameters.length === 0 ? (
                  <Alert borderRadius="md" bg="orange.50" borderColor="orange.200">
                    <AlertIcon color="orange.500" />
                    <Text>No input parameters defined for this document.</Text>
                  </Alert>
                ) : null}

              </>
            </Box>

            {/* Button Container */}
            <Box className="button-container" 
                 borderTop="1px" 
                 borderColor={borderColor} 
                 p={4} 
                 bg={cardBg}
                 flexShrink={0}>
              {showSubmitButton && !isChatApplication && (
                isProcessing ? (
                  <Box className="execute-loading-indicator">
                    <Center w="full">
                      <HStack spacing={3}>
                      </HStack>
                    </Center>
                  </Box>
                ) : (
                  <Button
                    className="execute-button"
                    colorScheme="primary"
                    size="md"
                    onClick={handleSubmit}
                    isDisabled={!parameters || parameters.length === 0}
                    leftIcon={<ArrowForwardIcon />}
                    fontWeight="bold"
                    fontSize="md"
                    borderRadius="lg"
                    boxShadow="md"
                  >
                    Ask {appInfo.name}
                  </Button>
                )
              )}
              {isChatApplication && (
                <Button
                  className="update-button"
                  colorScheme="primary"
                  size="md"
                  onClick={handleUpdateInputs}
                  isDisabled={!parameters || parameters.length === 0}
                  leftIcon={<ArrowForwardIcon />}
                  fontWeight="bold"
                  fontSize="md"
                  borderRadius="lg"
                  boxShadow="md"
                >
                  Update Inputs
                </Button>
              )}
            </Box>
          </Collapse>
        </Box>
      </Box>
    </Box>
  );
});